import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
  Container,
  Card,
  CardContent,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  LocalParking as ParkingIcon,
  DirectionsCar as CarIcon,
  Assignment as AssignIcon,
  Clear as ReleaseIcon,
  Create as CreateIcon,
  Refresh as RefreshIcon,
  Map as MapIcon,
  Settings as SettingsIcon,
  ViewModule as ViewModuleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchVehicles } from '../store/vehiclesSlice';
import { selectVehiclesList } from '../store/selectors';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import CurrentLayoutDisplay from '../components/CurrentLayoutDisplay';
import type { ParkingSpot } from '../types';

const SPOT_TYPE_COLORS = {
  regular: '#2196f3',
  maintenance: '#ff9800',
  washing: '#9c27b0',
  premium: '#4caf50',
};

const SPOT_TYPE_LABELS = {
  regular: 'Standard',
  maintenance: 'Maintenance',
  washing: 'Lavage',
  premium: 'Premium',
};

const AssignVehicleDialog: React.FC<{
  open: boolean;
  spot: ParkingSpot | null;
  vehicles: any[];
  parkingSpots: ParkingSpot[];
  onClose: () => void;
  onAssign: (spotId: string, vehicleId: string) => void;
  onRelease: (spot: ParkingSpot) => void;
  loading: boolean;
}> = ({ open, spot, vehicles, parkingSpots, onClose, onAssign, onRelease, loading }) => {
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);

  useEffect(() => {
    if (!open) {
      setSelectedVehicleId('');
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (spot && selectedVehicleId) {
      onAssign(spot.id.toString(), selectedVehicleId);
    }
  };

  // Get all vehicles with their assignment status
  const vehiclesWithStatus = vehicles.map(vehicle => {
    // Check if this vehicle is assigned to any parking spot
    // The backend uses place_parking_id on the vehicle, not assigned_vehicle_id on the spot
    const assignedSpot = parkingSpots.find(spot => {
      console.log('Checking spot:', spot.id, 'vehicle.place_parking_id:', vehicle.place_parking_id, 'vehicle.id:', vehicle.id);
      return vehicle.place_parking_id && vehicle.place_parking_id.toString() === spot.id.toString();
    });
    
    console.log('Vehicle:', vehicle.registration_number, 'place_parking_id:', vehicle.place_parking_id, 'assignedSpot:', assignedSpot);
    
    return {
      ...vehicle,
      isAssigned: !!assignedSpot,
      assignedSpot: assignedSpot
    };
  });

  // Filter and sort vehicles based on toggle
  const filteredVehicles = showOnlyAvailable 
    ? vehiclesWithStatus.filter(v => !v.isAssigned)
    : vehiclesWithStatus;
    
  const sortedVehicles = filteredVehicles.sort((a, b) => {
    if (a.isAssigned && !b.isAssigned) return 1;
    if (!a.isAssigned && b.isAssigned) return -1;
    return 0;
  });

  const isOccupied = spot?.is_occupied && (spot as any)?.vehicle;
  const assignedVehicle = (spot as any)?.vehicle;
  
  console.log('Dialog - Spot data:', spot);
  console.log('Dialog - Is occupied:', isOccupied);
  console.log('Dialog - Assigned vehicle:', assignedVehicle);
  console.log('Dialog - All vehicles with status:', sortedVehicles);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ 
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: isOccupied ? 'error.main' : 'primary.main' }}>
              {isOccupied ? <ReleaseIcon /> : <AssignIcon />}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
          {isOccupied ? `Place ${spot?.spot_number} - Occupée` : `Assigner un véhicule à la place ${spot?.spot_number}`}
              </Typography>
            <Typography variant="body2" color="text.secondary">
              {spot?.location}
            </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {isOccupied ? (
              // Show current assignment details
              <Box>
                <Typography variant="h6" gutterBottom>
                  Véhicule actuellement assigné:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1, 
                  backgroundColor: '#f5f5f5' 
                }}>
                  <Typography variant="body1" fontWeight="bold">
                    {assignedVehicle?.registration_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {assignedVehicle?.marque} {assignedVehicle?.modele}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Année: {assignedVehicle?.year}
                  </Typography>
                </Box>
              </Box>
            ) : (
              // Show vehicle selection for available spot
              <>
                {/* Filter toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={showOnlyAvailable}
                      onChange={(e) => setShowOnlyAvailable(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Afficher seulement les véhicules disponibles"
                />
                
                <FormControl fullWidth>
                  <InputLabel>Sélectionner un véhicule</InputLabel>
                  <Select
                    value={selectedVehicleId}
                    onChange={(e) => setSelectedVehicleId(e.target.value)}
                    required
                  >
                    {sortedVehicles.map(vehicle => (
                      <MenuItem 
                        key={vehicle.id} 
                        value={vehicle.id}
                        disabled={vehicle.isAssigned}
                        sx={{
                          opacity: vehicle.isAssigned ? 0.6 : 1,
                          '&.Mui-disabled': {
                            opacity: 0.6
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <Box>
                            <Typography variant="body2">
                              {vehicle.registration_number} - {vehicle.marque} {vehicle.modele}
                            </Typography>
                            {vehicle.isAssigned && (
                              <Typography variant="caption" color="text.secondary">
                                Assigné à la place {vehicle.assignedSpot?.spot_number}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ ml: 2 }}>
                            {vehicle.isAssigned ? (
                              <Chip 
                                label="Assigné" 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                              />
                            ) : (
                              <Chip 
                                label="Disponible" 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Summary information */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {showOnlyAvailable ? 'Véhicules disponibles:' : 'Résumé des véhicules:'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`${vehiclesWithStatus.filter(v => !v.isAssigned).length} Disponibles`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    {!showOnlyAvailable && (
                      <Chip 
                        label={`${vehiclesWithStatus.filter(v => v.isAssigned).length} Assignés`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
                
                {vehiclesWithStatus.filter(v => !v.isAssigned).length === 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Aucun véhicule disponible. Tous les véhicules sont déjà assignés à des places.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Annuler
          </Button>
          {isOccupied ? (
            <Button
              onClick={() => spot && onRelease(spot)}
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                },
                '&:disabled': {
                  background: 'grey.300',
                }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Libérer la place'}
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !selectedVehicleId}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                },
                '&:disabled': {
                  background: 'grey.300',
                }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Assigner'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Parking: React.FC = () => {
  const dispatch = useAppDispatch();
  const vehicles = useAppSelector(selectVehiclesList);
  const navigate = useNavigate();

  const [parkingSpots, setParkingSpots] = useState<ParkingSpot[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState<ParkingSpot | null>(null);
  
  // Parking layouts (plans) management
  const [availableLayouts, setAvailableLayouts] = useState<any[]>([]);
  const [currentLayout, setCurrentLayout] = useState<any>(null);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null);

  // Load available parking layouts
  const loadAvailableLayouts = async () => {
    setLoadingLayout(true);
    try {
      console.log('Loading parking layouts...');
      const response = await api.get('/parking-layouts');
      const layouts = response.data.data || response.data || [];
      console.log('Available layouts:', layouts);
      
      // Filter to show only active plans
      const activeLayouts = layouts.filter((layout: any) => layout.is_active);
      setAvailableLayouts(activeLayouts);
      
      // Set default layout (first active plan)
      const defaultLayout = activeLayouts[0];
      
      if (defaultLayout) {
        setSelectedLayoutId(defaultLayout.id.toString());
        await loadLayoutData(defaultLayout.id.toString());
      } else {
        setCurrentLayout(null);
      }
    } catch (err) {
      console.error('Error loading layouts:', err);
      setCurrentLayout(null);
    } finally {
      setLoadingLayout(false);
    }
  };

  // Load specific layout data
  const loadLayoutData = async (layoutId: string) => {
    try {
      console.log('Loading layout data for ID:', layoutId);
      const layoutResponse = await api.get(`/parking-layouts/${layoutId}`);
      const fullLayoutData = layoutResponse.data;
      console.log('Full layout data:', fullLayoutData);
      
      // The API returns {data: {...}} structure, so we need to access the nested data
      const layoutData = fullLayoutData.data || fullLayoutData;
      console.log('Layout data:', layoutData);
      console.log('Settings field:', layoutData.settings);
      
      // Extract visual data from settings
      const settings = layoutData.settings || {};
      console.log('Parsed settings:', settings);
      const visualSpots = settings.visual_spots || [];
      const visualElements = settings.visual_elements || [];
      
      console.log('Visual spots:', visualSpots);
      console.log('Visual elements:', visualElements);
      
      // Create the layout object with visual data
      const layoutWithVisualData = {
        ...layoutData,
        spots: visualSpots,
        elements: visualElements,
        canvas: {
          width: layoutData.canvas_width || 1400,
          height: layoutData.canvas_height || 900,
          background_color: layoutData.background_color || '#ffffff',
        }
      };
      
      console.log('Final layout with visual data:', layoutWithVisualData);
      setCurrentLayout(layoutWithVisualData);
    } catch (err) {
      console.error('Error loading layout data:', err);
      setCurrentLayout(null);
    }
  };

  // Load data
  useEffect(() => {
    Promise.all([
      loadParkingSpots(),
      dispatch(fetchVehicles({})),
      loadAvailableLayouts(),
    ]).finally(() => setLoading(false));
  }, [dispatch]);

  // Handle layout selection change
  const handleLayoutChange = async (layoutId: string) => {
    setSelectedLayoutId(layoutId);
    await loadLayoutData(layoutId);
  };


  const loadParkingSpots = async () => {
    try {
      const response = await api.get('/parking-spots');
      console.log('Parking spots response:', response.data);
      setParkingSpots(response.data.data);
    } catch (err) {
      console.error('Error loading parking spots:', err);
      setError('Erreur lors du chargement des places de parking');
    }
  };

  const handleAssignVehicle = async (spotId: string, vehicleId: string) => {
    setActionLoading(true);
    try {
      await api.patch(`/parking-spots/${spotId}/assign`, { vehicle_id: vehicleId });
      
      // Reload parking spots and update selected spot
      const response = await api.get('/parking-spots');
      const updatedSpots = response.data.data;
      setParkingSpots(updatedSpots);
      
      // Update the selected spot with fresh data
      const updatedSpot = updatedSpots.find((s: ParkingSpot) => s.id.toString() === spotId);
      if (updatedSpot) {
        setSelectedSpot(updatedSpot);
      }
    } catch (err) {
      console.error('Error assigning vehicle:', err);
      setError('Erreur lors de l\'assignation du véhicule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReleaseVehicle = async (spot: ParkingSpot) => {
    setActionLoading(true);
    try {
      await api.patch(`/parking-spots/${spot.id}/release`);
      
      // Reload parking spots and update selected spot
      const response = await api.get('/parking-spots');
      const updatedSpots = response.data.data;
      setParkingSpots(updatedSpots);
      
      // Update the selected spot with fresh data
      const updatedSpot = updatedSpots.find((s: ParkingSpot) => s.id === spot.id);
      if (updatedSpot) {
        setSelectedSpot(updatedSpot);
      }
    } catch (err) {
      console.error('Error releasing vehicle:', err);
      setError('Erreur lors de la libération de la place');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle spot click for assignment
  const handleSpotClick = (spotData: any) => {
    console.log('Spot clicked:', spotData);
    console.log('Available parking spots:', parkingSpots);
    
    // Find the real parking spot data
    const realSpot = parkingSpots.find(spot => spot.id.toString() === spotData.assigned_spot_id?.toString());
    console.log('Found real spot:', realSpot);
    
    if (realSpot) {
      setSelectedSpot(realSpot);
      setShowAssignDialog(true);
    } else {
      console.log('No real spot found for visual spot:', spotData);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        height: 'calc(100vh - 64px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Box sx={{ px: 4, py: 4, flex: 1, overflow: 'hidden', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress size={60} />
          </Box>
        </Box>
      </Box>
    );
  }

  // Calculate parking statistics
  const totalSpots = parkingSpots.length;
  const occupiedSpots = parkingSpots.filter(spot => spot.is_occupied).length;
  const availableSpots = totalSpots - occupiedSpots;
  const occupancyRate = totalSpots > 0 ? Math.round((occupiedSpots / totalSpots) * 100) : 0;

  return (
      <Box sx={{ 
      height: 'calc(100vh - 64px)',
      width: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{ px: 4, py: 4, flex: 1, overflow: 'hidden', width: '100%' }}>
        {/* Compact Header Section */}
        <Box sx={{ mb: 2, width: '100%' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1, width: '100%', gap: 2, minWidth: '100%' }}>
            <Box sx={{ flexShrink: 0, minWidth: 'fit-content' }}>
              <Typography variant="body2" color="text.secondary">
                {totalSpots} places • {occupiedSpots} occupées • {availableSpots} disponibles • {occupancyRate}% occupation
              </Typography>
            </Box>
            <FormControl sx={{ flex: '10 1 auto', minWidth: 0, maxWidth: 'none', width: '100%' }}>
              <InputLabel>Plan de Parking</InputLabel>
              <Select
                value={selectedLayoutId || ''}
                onChange={(e) => handleLayoutChange(e.target.value)}
                label="Plan de Parking"
                size="small"
                sx={{ 
                  borderRadius: 2,
                  width: '100%',
                  minWidth: '100%',
                  '& .MuiSelect-select': {
                    width: '100%',
                    minWidth: '100%'
                  }
                }}
              >
                {availableLayouts.map((layout) => (
                  <MenuItem key={layout.id} value={layout.id.toString()}>
                    {layout.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, minWidth: 'fit-content' }}>
              <Tooltip title="Actualiser">
                <IconButton 
                  onClick={() => {
                    loadParkingSpots();
                    loadAvailableLayouts();
                  }}
                  disabled={loading}
                  size="small"
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'grey.300' }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<CreateIcon />}
                onClick={() => navigate('/parking/editor')}
                size="small"
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                  },
                }}
              >
                Éditeur
              </Button>
            </Stack>
          </Stack>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }} 
              onClose={() => setError(null)}
              action={
                <Button color="inherit" size="small" onClick={() => {
                  loadParkingSpots();
                  loadAvailableLayouts();
                }}>
                  Réessayer
                </Button>
              }
            >
              {error}
            </Alert>
          )}
        </Box>


        {/* Main Content - Plan Display */}
        <Card sx={{ 
          borderRadius: 2, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          minHeight: 0,
          height: 'calc(100vh - 180px)',
          width: '100%',
          maxWidth: 'none'
        }}>
        {loadingLayout ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flex: 1
            }}>
              <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} />
                <Typography variant="body2" color="text.secondary">
                  Chargement du plan de parking...
                </Typography>
              </Stack>
          </Box>
        ) : currentLayout ? (
            <Box sx={{ 
              width: '100%', 
              height: '100%',
              overflow: 'hidden',
              flex: 1,
              position: 'relative',
              minWidth: 0
            }}>
            <CurrentLayoutDisplay 
              layout={currentLayout} 
              parkingSpots={parkingSpots}
              onSpotClick={handleSpotClick}
            />
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            textAlign: 'center',
            height: '100%',
            flex: 1,
            p: 4
          }}>
              <ViewModuleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucun plan de parking actif
            </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
                Aucun plan n'est actuellement actif. Utilisez l'éditeur pour créer et activer des plans de parking.
            </Typography>
            <Button
              variant="contained"
              startIcon={<CreateIcon />}
              onClick={() => navigate('/parking/editor')}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Créer un Plan
            </Button>
          </Box>
        )}
        </Card>
      </Box>

      {/* Dialogs */}
      <AssignVehicleDialog
        open={showAssignDialog}
        spot={selectedSpot}
        vehicles={vehicles}
        parkingSpots={parkingSpots}
        onClose={() => {
          setShowAssignDialog(false);
          setSelectedSpot(null);
        }}
        onAssign={handleAssignVehicle}
        onRelease={handleReleaseVehicle}
        loading={actionLoading}
      />
    </Box>
  );
};

export default Parking;